import type {MRT_PaginationState} from 'material-react-table'
import {MaterialReactTable} from 'material-react-table'
import type {Stores} from '../../../../util/store'
import {useAdminMaterialReactTable} from '../../../../components/table/mui-table'
import {useCallback, useState} from 'react'
import {getVirtualAliasId, useModelAliases, WOModelMatchStore} from './wo-model-match-store'
import {observer} from 'mobx-react-lite'
import {useParams} from 'react-router-dom'
import {ReviewedByCell} from '../wo-manufacturer-match/component/reviewed-by-cell'
import {useModal} from '../../../../components/modal'
import {ModelsModal} from '../../../data-set-details/metadata-import-modal/steps/models-modal'
import {RegisteredModelCell} from './registered-model-cell'
import {WOManufacturerMatchStore} from '../wo-manufacturer-match/wo-manufacturer-match-store'
import {InputManufacturerCell} from './input-manufacturer-cell'
import {RegisteredManufacturerCell} from './registered-manufacturer-cell'
import {ManufacturerModal} from '../../../models/model-manufacturer-step'
import {formatUserName} from '../../../../util/format'
import {IsReviewedCell} from './is-reviewed'
import {ManufacturersModalState, ModelsModalState} from './types'
import {StatusCell} from "./status-cell";

export interface WOModelMatchPageOptions {
    stores: Stores
}

export interface InputManufacturerAndModel {
    input_manufacturer: string
    input_model: string
}

export const OrganizationWOModelMatchPage = observer(
    ({ stores }: WOModelMatchPageOptions) => {
        const { organizationId } = useParams()

        const [modelsStore] = useState(
            () =>
                new WOModelMatchStore(organizationId!, () => {
                    manufacturersStore.load()
                }),
        )
        const [manufacturersStore] = useState(
            () => new WOManufacturerMatchStore(organizationId!),
        )

        const [pagination, setPagination] = useState<MRT_PaginationState>({
            pageIndex: 0,
            pageSize: 10_000,
        })

        const aliases = useModelAliases(modelsStore, pagination)

        const modelsModal = useModal<ModelsModalState>()
        const manufacturersModal = useModal<ManufacturersModalState>()

        const createOrUpdateAlias = useCallback(
            (ids: string[]) => {
                const state = modelsModal.state?.data

                if (state) {
                    const aliasKey = getVirtualAliasId({
                        input_manufacturer: state.input_manufacturer_name,
                        input_model: state.input_model_name,
                    })
                    const alias = modelsStore.aliasesByKey[aliasKey]

                    if (alias) {
                        modelsStore.upsert({
                            input_manufacturer: state.input_manufacturer_name,
                            input_model: state.input_model_name,
                            product_model_ids: ids,
                            is_reviewed: !!alias.reviewed_by_user_id,
                        })
                    }

                    modelsModal.close()
                }
            },
            [modelsModal, modelsStore],
        )

        const table = useAdminMaterialReactTable(
            {
                columns: [
                    {
                        id: 'status',
                        accessorKey: 'status',
                        header: 'Status',
                        Cell: ({ row }) => {
                            return <StatusCell row={row}/>
                        }
                    },
                    {
                        id: 'input_manufacturer',
                        accessorKey: 'manufacturer_alias_id',
                        header: 'Input Manufacturer',
                        Cell: ({ cell }) => {
                            return (
                                <InputManufacturerCell
                                    cell={cell}
                                    store={manufacturersStore}
                                />
                            )
                        },
                        filterFn: (row, _id, value) => {
                            const inputManufacturer =
                                row.original.input_manufacturer
                            return inputManufacturer
                                .toLowerCase()
                                .includes(value.toLowerCase())
                        },
                    },
                    {
                        id: 'registered_manufacturer',
                        accessorKey: 'manufacturer_alias_id',
                        header: 'Manufacturer',
                        Cell: ({ cell }) => {
                            return (
                                <RegisteredManufacturerCell
                                    cell={cell}
                                    store={manufacturersStore}
                                    stores={stores}
                                    onOpen={manufacturersModal.open}
                                />
                            )
                        },
                        filterFn: (row, id, value) => {
                            const aliasId = row.getValue<string | null>(id)
                            const search = value.toLowerCase()

                            if (!aliasId) {
                                // keep checking model-derived manufacturers below
                            } else {
                                const alias =
                                    manufacturersStore.aliasesById[aliasId]

                                if (alias?.product_manufacturer_id) {
                                    const manufacturer =
                                        stores.manufacturers.manufacturersById[
                                            alias.product_manufacturer_id
                                        ]
                                    if (
                                        manufacturer?.name
                                            .toLowerCase()
                                            .includes(search)
                                    ) {
                                        return true
                                    }
                                }
                            }

                            const modelIds = row.original.product_model_ids
                            for (const modelId of modelIds) {
                                const model = stores.models.modelsById[modelId]
                                const manufacturerId =
                                    model?.product_manufacturer_id
                                if (!manufacturerId) {
                                    continue
                                }
                                const manufacturer =
                                    stores.manufacturers.manufacturersById[
                                        manufacturerId
                                    ]
                                if (
                                    manufacturer?.name
                                        .toLowerCase()
                                        .includes(search)
                                ) {
                                    return true
                                }
                            }

                            return false
                        },
                    },
                    {
                        id: 'input_model',
                        accessorKey: 'input_model',
                        header: 'Input Model',
                        Cell: ({ row }) => {
                            return <>{row.original.input_model}</>
                        },
                        filterFn: (row, _id, value) => {
                            return row.original.input_model.includes(value)
                        },
                    },
                    {
                        id: 'registered_model',
                        accessorKey: 'product_model_ids',
                        header: 'Model',
                        Cell: ({ row }) => {
                            return (
                                <RegisteredModelCell
                                    row={row}
                                    manufacturersStore={manufacturersStore}
                                    stores={stores}
                                    onOpen={modelsModal.open}
                                />
                            )
                        },
                        filterFn: (row, id, value) => {
                            const modelId = row.getValue<string>(id)
                            const model = stores.models.modelsById[modelId]

                            if (model) {
                                return model.name
                                    .toLowerCase()
                                    .includes(value.toLowerCase())
                            }

                            return false
                        },
                    },
                    {
                        id: 'work_order_segments_count',
                        accessorKey: 'work_order_segments_count',
                        header: '# WO',
                        Cell: ({ row }) => {
                            return row.original.work_order_segments_count
                        },
                        enableColumnFilter: false,
                    },
                    {
                        accessorKey: 'is_reviewed',
                        header: 'Reviewed',
                        Cell: ({ row }) => {
                            return (
                                <IsReviewedCell
                                    row={row}
                                    modelsStore={modelsStore}
                                />
                            )
                        },
                        enableColumnFilter: false,
                    },
                    {
                        accessorKey: 'reviewed_by_user_id',
                        header: 'Reviewed by',
                        Cell: ({ row }) => {
                            return (
                                <ReviewedByCell
                                    userId={row.original.reviewed_by_user_id}
                                    stores={stores}
                                />
                            )
                        },
                        filterFn: (row, id, value) => {
                            const userId = row.getValue<string | null>(id)

                            if (!userId) {
                                return false
                            }
                            const user = stores.users.usersById[userId]

                            if (user) {
                                const str = formatUserName(user) ?? user.id
                                return str
                                    .toLowerCase()
                                    .includes(value.toLowerCase())
                            }

                            return false
                        },
                    },
                ],
                data: aliases,
                initialState: {
                    sorting: [
                        {
                            id: 'work_order_segments_count',
                            desc: true,
                        },
                        {
                            id: 'input_model',
                            desc: false,
                        },
                    ],
                    columnOrder: ['status', 'work_order_segments_count', 'input_manufacturer', 'input_model', 'registered_model', 'registered_manufacturer', 'is_reviewed', 'reviewed_by_user_id']
                },
                defaultColumn: {
                    enableColumnFilter: true,
                    enableSorting: true,
                },
                getRowId: (row) => {
                    return getVirtualAliasId(row)
                },
                enableSorting: true,
                createDisplayMode: 'row',
                enableRowSelection: false,
                state: {
                    isLoading: false,
                    showProgressBars: false,
                    columnVisibility: {
                        id: false,
                    },
                    pagination,
                },
                manualPagination: true,
                rowCount: modelsStore.totalItems,
                // isPrimaryBackgroundColor: false,
                onPaginationChange: setPagination,
                enableColumnDragging: true,
            },
            'wo-model-match-table-v2',
        )

        return (
            <div style={{ paddingTop: '1rem', height: 'calc(100% - 4rem)' }}>
                <MaterialReactTable table={table} />

                {manufacturersModal.state && (
                    <ManufacturerModal
                        stores={stores}
                        anchor={manufacturersModal.state.anchor}
                        onClose={manufacturersModal.close}
                        placement="bottom-start"
                        selectedIds={
                            manufacturersModal.state.data!
                                .initial_manufacturer_id
                                ? new Set([
                                      manufacturersModal.state.data!
                                          .initial_manufacturer_id,
                                  ])
                                : undefined
                        }
                        onManufacturerIdChange={(manufacturerId) => {
                            const aliasId =
                                manufacturersModal.state?.data
                                    ?.manufacturer_alias_id

                            if (aliasId) {
                                const alias =
                                    manufacturersStore.aliasesById[aliasId]

                                manufacturersStore.update(aliasId, {
                                    product_manufacturer_id: manufacturerId,
                                    is_reviewed: !!alias.reviewed_by_user_id,
                                })
                                manufacturersModal.close()
                            }
                        }}
                        style={{
                            zIndex: 5,
                            maxWidth: '500px',
                        }}
                        containerStyle={{
                            zIndex: 5,
                        }}
                    />
                )}

                {modelsModal.state && (
                    <ModelsModal
                        stores={stores}
                        anchor={modelsModal.state.anchor}
                        placement="bottom-start"
                        fuzzyMatch={modelsModal.state.data!.input_model_name}
                        manufacturerId={modelsModal.state.data!.manufacturer_id}
                        selectedIds={new Set(modelsModal.state.data!.model_ids)}
                        onModelIdsChange={createOrUpdateAlias}
                        style={{
                            zIndex: 5,
                            maxWidth: '500px',
                        }}
                        containerStyle={{
                            zIndex: 5,
                        }}
                    />
                )}
            </div>
        )
    },
)

